import { createPatientTimelineApi } from '@/api/_patient-consultation-timeline'
import { createTimelineState, createTimelineSession, timelineDate, timelineError, editorLocation } from '@/utils/patient-consultation-timeline'
import { resume_consultation_impression } from '@/utils/resume-consultation-print'
import TimelineFilterBar from './timeline-filter-bar.vue'
import ConsultationTimelineItem from './consultation-timeline-item.vue'
import TimelineDocumentPreview from './timeline-document-preview.vue'
import TimelineClinicalNotes from './timeline-clinical-notes.vue'

export default {
    name: 'PatientConsultationTimeline',
    components: { TimelineFilterBar, ConsultationTimelineItem, TimelineDocumentPreview, TimelineClinicalNotes },
    props: { patientId: { type: Number, required: true }, patient: { type: Object, required: true } },
    data() {
        return { state: createTimelineState(), filters: { type: 'ALL', q: '', year: 'ALL', page: 1, per_page: 20 },
            opened: {}, years: [], loadedContext: '', modal: { open: false, type: '', id: null, loading: false, error: '', detail: null },
            reportHtml: '', reportReady: false, printing: false, printAfterLoad: false, actionError: '', navigating: null, returnFocus: null }
    },
    computed: {
        actorKey() {
            const auth = this.$store.getters.authInfo || {}, role = this.$store.getters.activeRole || {}
            return [this.patientId, auth.id || '', auth.access_token || '', role.id || '', role.nom || ''].join('|')
        },
        canUseTimeline() {
            const auth = this.$store.getters.authInfo || {}, role = this.$store.getters.activeRole || {}
            return !!auth.id && !!auth.access_token && ['ROLE_MEDECIN', 'ROLE_INFIRMIER', 'ROLE_SAGE_FEMME'].includes(role.nom)
        },
        contextCurrent() { return this.loadedContext === this.actorKey && Number(this.patient.id) === this.patientId && this.canUseTimeline },
        timezone() { return this.state.meta && this.state.meta.timezone || '' },
        patientName() { return [this.patient.nom, this.patient.prenoms].filter(Boolean).join(' ') },
        filtered() { return this.filters.type !== 'ALL' || this.filters.year !== 'ALL' || !!this.filters.q.trim() },
        previewReference() { const item = this.state.items.find(item => Number(item.id) === Number(this.state.preview.consultationId)); return item && item.reference_bon || '' },
        modalTitle() { return (this.modal.type === 'report' ? 'Compte rendu' : 'Dossier en lecture seule') + (this.modal.detail && this.modal.detail.summary.reference_bon ? ' · ' + this.modal.detail.summary.reference_bon : '') }
    },
    watch: {
        actorKey() { this.resetContext(); if (this.canUseTimeline) this.load() },
        'state.error'(error) { if (error) this.clearViews() }
    },
    created() {
        const api = createPatientTimelineApi(() => ({ id: (this.$store.getters.authInfo || {}).id, key: this.actorKey }))
        this._session = createTimelineSession(api, this.state, () => this.actorKey)
        this._modalGeneration = 0
        if (this.canUseTimeline) this.load()
    },
    beforeDestroy() { clearTimeout(this._searchTimer); this.clearViews(); this._session.dispose() },
    methods: {
        date(value) { return timelineDate(value, this.timezone) },
        clearViews() {
            this._modalGeneration = (this._modalGeneration || 0) + 1
            this.opened = {}; this.reportHtml = ''; this.reportReady = false; this.printAfterLoad = false; this.printing = false
            this.modal = { open: false, type: '', id: null, loading: false, error: '', detail: null }
            this.navigating = null; this.actionError = ''
        },
        resetContext() {
            clearTimeout(this._searchTimer); this._session.reset(); this.clearViews(); this.years = []
            this.filters = { type: 'ALL', q: '', year: 'ALL', page: 1, per_page: 20 }; this.loadedContext = ''
        },
        async load() {
            clearTimeout(this._searchTimer); this.clearViews(); this.loadedContext = this.actorKey
            const result = await this._session.load(this.patientId, { ...this.filters })
            if (result) this.years = result.meta.years || []
        },
        changeFilter(key, value) {
            this.filters = { ...this.filters, [key]: value, page: 1 }; this.load()
        },
        search(value) {
            this.filters = { ...this.filters, q: value.slice(0, 200), page: 1 }
            clearTimeout(this._searchTimer)
            // Effacer immédiatement les anciens contenus pendant la temporisation.
            this._session.reset(); this.clearViews(); this.state.loading = true
            this._searchTimer = setTimeout(() => this.load(), 300)
        },
        page(value) { if (this.state.loading) return; this.filters = { ...this.filters, page: value }; this.load() },
        async toggle(item) {
            this.opened = { ...this.opened, [item.id]: !this.opened[item.id] }
            if (this.opened[item.id]) await this.retryDetail(item)
        },
        async retryDetail(item) {
            try { await this._session.details(this.patientId, item.id) } catch (error) { /* L’erreur de carte est conservée dans l’état local. */ }
        },
        preview(item, type, target) {
            this.closeRecord(); this.returnFocus = target
            this._session.preview(this.patientId, item.id, type)
        },
        retryPreview() { const p = this.state.preview; this._session.preview(this.patientId, p.consultationId, p.type) },
        closePreview() { this._session.closePreview() },
        closeRecord() {
            this._modalGeneration = (this._modalGeneration || 0) + 1
            this.modal.open = false; this.modal.detail = null; this.reportHtml = ''; this.reportReady = false
            this.printAfterLoad = false; this.printing = false
        },
        async record(item, type, target) {
            this.closePreview(); this.returnFocus = target || this.returnFocus
            const context = this.actorKey, generation = ++this._modalGeneration
            this.reportHtml = ''; this.reportReady = false; this.printAfterLoad = false
            this.modal = { open: true, type, id: item.id, loading: true, error: '', detail: null }
            try {
                const detail = await this._session.details(this.patientId, item.id, true)
                if (!detail || context !== this.actorKey || generation !== this._modalGeneration || !this.modal.open) return
                if (!detail.summary.permissions || !detail.summary.permissions.can_view_details || (type === 'report' && !detail.summary.permissions.can_print)) throw new Error('Accès indisponible')
                this.modal.detail = detail
                if (type === 'report') this.reportHtml = this.buildReport(detail)
            } catch (error) {
                if (context === this.actorKey && generation === this._modalGeneration) this.modal.error = timelineError(error)
            } finally {
                if (context === this.actorKey && generation === this._modalGeneration) this.modal.loading = false
            }
        },
        retryRecord() { this.record({ id: this.modal.id }, this.modal.type) },
        buildReport(detail) {
            if (!this.contextCurrent || Number(detail.patient_id) !== this.patientId || Number(detail.consultation_id) !== Number(this.modal.id)) throw new Error('Contexte du dossier incohérent')
            const summary = detail.summary
            return resume_consultation_impression(detail.historiques, { patient: this.patient,
                consultation: { reference_bon: summary.reference_bon, type_consultation: summary.specialty && summary.specialty.name },
                evolutions: detail.evolutions, entete: this.$store.getters.config_info_clinique || {}, mode: 'complet' })
        },
        async printReport() {
            if (this.printing || this.modal.loading || !this.modal.open || !this.modal.detail) return
            this.printing = true; this.modal.error = ''; this.reportReady = false; this.reportHtml = ''
            const context = this.actorKey, generation = this._modalGeneration, id = this.modal.id
            try {
                // Réévaluer l’accès serveur au moment d’imprimer, même si l’aperçu était ouvert.
                const detail = await this._session.details(this.patientId, id, true)
                if (!detail || context !== this.actorKey || generation !== this._modalGeneration || !this.modal.open) return
                if (!detail.summary.permissions || !detail.summary.permissions.can_print) throw new Error('Accès indisponible')
                this.modal.detail = detail; this.printAfterLoad = true; this.reportHtml = this.buildReport(detail)
            } catch (error) {
                if (context === this.actorKey && generation === this._modalGeneration) this.modal.error = timelineError(error)
                this.printing = false
            }
        },
        reportLoaded() {
            if (!this.reportHtml || !this.contextCurrent || !this.modal.open) return
            this.reportReady = true
            if (!this.printAfterLoad) return
            this.printAfterLoad = false
            const frame = this.$refs.reportFrame
            try { if (frame && frame.contentWindow) { frame.contentWindow.focus(); frame.contentWindow.print() } }
            finally { this.printing = false }
        },
        async editor(item) {
            if (this.navigating != null) return
            const role = this.$store.getters.activeRole || {}
            if (role.nom !== 'ROLE_MEDECIN') return
            const context = this.actorKey
            this.navigating = item.id; this.actionError = ''
            try {
                const detail = await this._session.details(this.patientId, item.id, true)
                if (!detail || context !== this.actorKey) return
                const location = editorLocation(detail.summary)
                if (!location) throw new Error('Accès indisponible')
                await this.$router.push(location)
            } catch (error) { if (context === this.actorKey) this.actionError = 'L’accès à l’éditeur n’est plus disponible. Actualisez l’historique.' }
            finally { if (context === this.actorKey) this.navigating = null }
        }
    }
}
